/** Export and import of sets as a JSON file, including the learning progress. */
import type { Card, VocabSet } from './types';
import { loadData, sanitizeSet, saveData } from './storage';
import { makeSetId } from './ids';

const EXPORT_VERSION = 1;

export type ExportFile = {
  app: 'voci';
  version: number;
  exportedAt: number;
  sets: VocabSet[];
};

export type ImportStrategy = 'merge' | 'replace';

export type ImportSummary = {
  setsAdded: number;
  setsMerged: number;
  cardsAdded: number;
  cardsUpdated: number;
};

function slugify(value: string): string {
  return (
    value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) || 'set'
  );
}

export function buildExport(sets: VocabSet[]): ExportFile {
  return { app: 'voci', version: EXPORT_VERSION, exportedAt: Date.now(), sets };
}

export function exportFileName(sets: VocabSet[]): string {
  const date = new Date().toISOString().slice(0, 10);
  const name = sets.length === 1 ? slugify(sets[0].name) : 'alle-sets';
  return `voci-${name}-${date}.json`;
}

export function downloadExport(sets: VocabSet[]): void {
  const blob = new Blob([JSON.stringify(buildExport(sets), null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = exportFileName(sets);
  document.body.appendChild(link);
  link.click();
  link.remove();

  // Give Safari a moment before the object url disappears.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function parseExportFile(text: string): VocabSet[] {
  const parsed = JSON.parse(text) as Partial<ExportFile>;
  if (!parsed || !Array.isArray(parsed.sets)) {
    throw new Error('Die Datei enthält keine Voci-Sets.');
  }

  const sets = parsed.sets
    .map(sanitizeSet)
    .filter((set): set is VocabSet => set !== null);

  if (sets.length === 0) {
    throw new Error('Die Datei enthält keine gültigen Sets.');
  }

  return sets;
}

/** Keeps the higher progress when the same card exists on both sides. */
function mergeCard(existing: Card, incoming: Card): Card {
  return {
    ...existing,
    box: Math.max(existing.box, incoming.box),
    asked: Math.max(existing.asked, incoming.asked),
    correct: Math.max(existing.correct, incoming.correct),
    lastAsked: Math.max(existing.lastAsked ?? 0, incoming.lastAsked ?? 0) || null,
  };
}

export function importSets(incomingSets: VocabSet[], strategy: ImportStrategy): ImportSummary {
  const data = loadData();
  const summary: ImportSummary = {
    setsAdded: 0,
    setsMerged: 0,
    cardsAdded: 0,
    cardsUpdated: 0,
  };

  for (const incoming of incomingSets) {
    const existing = data.sets.find(
      (set) => set.id === incoming.id || set.name.trim().toLowerCase() === incoming.name.trim().toLowerCase(),
    );

    if (!existing) {
      data.sets.push({ ...incoming, id: incoming.id || makeSetId(), updatedAt: Date.now() });
      summary.setsAdded += 1;
      summary.cardsAdded += incoming.cards.length;
      continue;
    }

    if (strategy === 'replace') {
      existing.cards = incoming.cards;
      existing.nativeLang = incoming.nativeLang;
      existing.foreignLang = incoming.foreignLang;
      existing.updatedAt = Date.now();
      summary.setsMerged += 1;
      summary.cardsUpdated += incoming.cards.length;
      continue;
    }

    const byId = new Map(existing.cards.map((card) => [card.id, card]));
    for (const card of incoming.cards) {
      const match = byId.get(card.id);
      if (match) {
        byId.set(card.id, mergeCard(match, card));
        summary.cardsUpdated += 1;
      } else {
        byId.set(card.id, card);
        summary.cardsAdded += 1;
      }
    }

    existing.cards = Array.from(byId.values());
    existing.updatedAt = Date.now();
    summary.setsMerged += 1;
  }

  saveData(data);
  return summary;
}
